/**
 * Lazy CheckoutSession bootstrapper for the Stripe Express Checkout Element. The session is
 * created on first wallet interaction (not page load) and memoized. On `{ redirect }` we redirect the
 * page; on `{ error }` we surface the message to the wallet event.
 *
 * No CSRF bootstrap is required: buyer PII is persisted via the webstore `express/persist` POST
 * (webstore CSRF), and completion is a Node GET protected by the unguessable XO session token in its
 * path (CSRF-exempt by method) -- so no Checkout-owned cookie is ever set on the cart page.
 */
import { ENDPOINTS } from './constants';
import request from './request';

let _sessionPromise = null;
let _sessionGeneration = 0;

/**
 * Returns a memoized Promise resolving to the session URLs and tokens. Callers may invoke this from
 * any wallet event handler without coordinating.
 *
 *   shippingUrl         -- Node `/checkout/paypal/shipping/{token}` (wallet-agnostic shipping pipeline).
 *   confirmUrl          -- Node `/checkout/rp-payments/confirm/{token}` (deferred-intent completion).
 *   rpPaymentsGatewayId -- RP Payments gateway id forwarded into confirm; may be null (Node falls back).
 *
 * @returns {Promise<{ sessionToken: string, shippingUrl: string, confirmUrl: string, rpPaymentsGatewayId: (number|null) }>}
 */
export default function getOrCreateSessionUrls() {
	if (_sessionPromise) {
		return _sessionPromise;
	}

	const generation = _sessionGeneration;

	_sessionPromise = request(ENDPOINTS.start, { method: 'POST', data: {} })
		.then((response) => {
			if (response?.redirect) {
				// Match PayPal: blocked buyers bounce to the cart with an error code.
				window.location.assign(response.redirect);
				const err = new Error('Express checkout blocked; redirecting');
				err.redirected = true;
				throw err;
			}
			if (!response?.shippingUrl || !response?.confirmUrl || !response?.sessionToken) {
				const message = response?.error?.message || 'Express checkout could not start.';
				const err = new Error(message);
				err.code = response?.error?.code;
				throw err;
			}
			return {
				confirmUrl: response.confirmUrl,
				rpPaymentsGatewayId: response.rpPaymentsGatewayId || null,
				sessionToken: response.sessionToken,
				shippingUrl: response.shippingUrl,
			};
		})
		.catch((err) => {
			// Only clear the cache for the current generation; resetSession() bumps it per click, so a
			// superseded in-flight start must not null a newer session's memo.
			if (_sessionGeneration === generation) {
				_sessionPromise = null;
			}
			throw err;
		});

	return _sessionPromise;
}

/**
 * Drops the memoized session so the next getOrCreateSessionUrls() mints a fresh one. Called per wallet
 * click so the session is always bound to the current cart (the start endpoint snapshots cart contents).
 *
 * @returns {void}
 */
export function resetSession() {
	_sessionGeneration += 1;
	_sessionPromise = null;
}

/**
 * Test hook -- resets memoization. Production code never needs this.
 */
export function _resetSessionForTesting() {
	_sessionGeneration = 0;
	_sessionPromise = null;
}
